import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { CHAT_FLOW_STEPS, type ChatFlowStep } from '../ChatFlowSteps';
import {
  CHAT_INPUT_MODES,
  type ChatInputMode,
} from '../constants/ChatInputModes';
import {
  CHAT_VIEW_STATES,
  type ChatViewState,
} from '../constants/ChatViewStates';

type CurrentChatMessage = Record<string, unknown>;

interface ChatViewPresentation {
  viewState: ChatViewState | null;
  inputMode: ChatInputMode | null;
}

interface CurrentChatState {
  loading: boolean;
  chatId: string | null;
  flowStep: ChatFlowStep | null;
  inputMode: ChatViewPresentation['inputMode'];
  flowSteps: ChatFlowStep[];
  isRunningStep: boolean;
  isBotTyping: boolean;
  isConnectionError: boolean;
  feedbackSubmitted: boolean;
  bottomSheetVisible: boolean;
}

interface CurrentChat extends CurrentChatState {
  viewState: ChatViewPresentation['viewState'];
  hasCurrentChat: boolean;
  isChatFlowStepBeforeQuestion: boolean;
}

interface SetCurrentChatSessionOpts {
  chatId?: string | null;
  messages?: CurrentChatMessage[];
}

const flowStepsBeforeQuestion: ChatFlowStep[] = [
  CHAT_FLOW_STEPS.chatQuestion,
  CHAT_FLOW_STEPS.chatInit,
  CHAT_FLOW_STEPS.showWelcomeMessage,
  CHAT_FLOW_STEPS.quickActionSelection,
];

const chatViewPresentationByFlowStep: Partial<
  Record<ChatFlowStep, ChatViewPresentation>
> = {
  [CHAT_FLOW_STEPS.requestCategory]: {
    viewState: CHAT_VIEW_STATES.category,
    inputMode: CHAT_INPUT_MODES.none,
  },
  [CHAT_FLOW_STEPS.requestName]: {
    viewState: CHAT_VIEW_STATES.name,
    inputMode: CHAT_INPUT_MODES.name,
  },
  [CHAT_FLOW_STEPS.requestEmail]: {
    viewState: CHAT_VIEW_STATES.email,
    inputMode: CHAT_INPUT_MODES.email,
  },
  [CHAT_FLOW_STEPS.chatQuestion]: {
    viewState: CHAT_VIEW_STATES.question,
    inputMode: CHAT_INPUT_MODES.question,
  },
  [CHAT_FLOW_STEPS.greeting]: {
    viewState: CHAT_VIEW_STATES.question,
    inputMode: CHAT_INPUT_MODES.question,
  },
  [CHAT_FLOW_STEPS.chat]: {
    viewState: CHAT_VIEW_STATES.chat,
    inputMode: CHAT_INPUT_MODES.chat,
  },
  [CHAT_FLOW_STEPS.requestVisitorRole]: {
    viewState: CHAT_VIEW_STATES.chat,
    inputMode: CHAT_INPUT_MODES.none,
  },
  [CHAT_FLOW_STEPS.requestIntentSelection]: {
    viewState: CHAT_VIEW_STATES.automation,
    inputMode: CHAT_INPUT_MODES.none,
  },
  [CHAT_FLOW_STEPS.makePrediction]: {
    viewState: CHAT_VIEW_STATES.automation,
    inputMode: CHAT_INPUT_MODES.restart,
  },
  [CHAT_FLOW_STEPS.connectingExpert]: {
    viewState: CHAT_VIEW_STATES.connectingToExpert,
    inputMode: CHAT_INPUT_MODES.chat,
  },
  [CHAT_FLOW_STEPS.rateBadChat]: {
    viewState: CHAT_VIEW_STATES.rateBad,
    inputMode: CHAT_INPUT_MODES.back,
  },
  [CHAT_FLOW_STEPS.rateChat]: {
    viewState: CHAT_VIEW_STATES.finished,
    inputMode: CHAT_INPUT_MODES.restart,
  },
  [CHAT_FLOW_STEPS.chatEnd]: {
    viewState: CHAT_VIEW_STATES.finished,
    inputMode: CHAT_INPUT_MODES.restart,
  },
  [CHAT_FLOW_STEPS.closeChat]: {
    viewState: CHAT_VIEW_STATES.rate,
    inputMode: CHAT_INPUT_MODES.none,
  },
};

const chatViewPresentation = (
  flowStep?: ChatFlowStep | null,
): ChatViewPresentation => {
  if (!flowStep) {
    return { viewState: null, inputMode: null };
  }

  return (
    chatViewPresentationByFlowStep[flowStep] ?? {
      viewState: CHAT_VIEW_STATES.booting,
      inputMode: CHAT_INPUT_MODES.none,
    }
  );
};

const initialCurrentChatState: CurrentChatState = {
  loading: false,
  chatId: null,
  flowStep: null,
  inputMode: null,
  flowSteps: [],
  isRunningStep: false,
  isBotTyping: false,
  isConnectionError: false,
  feedbackSubmitted: false,
  bottomSheetVisible: false,
};

const currentChatStateVar = makeVar(initialCurrentChatState);
const currentChatMessagesVar = makeVar<CurrentChatMessage[]>([]);

const isChatFlowStep = (value: unknown): value is ChatFlowStep =>
  typeof value === 'string' &&
  Object.values(CHAT_FLOW_STEPS).includes(value as ChatFlowStep);

const updateCurrentChatState = (values: Partial<CurrentChatState>) => {
  currentChatStateVar({ ...currentChatStateVar(), ...values });
};

const resetCurrentChat = () => {
  currentChatStateVar({ ...initialCurrentChatState, flowSteps: [] });
  currentChatMessagesVar([]);
};

const setChatLoading = (loading: boolean) =>
  updateCurrentChatState({ loading });

const setBotTyping = (isBotTyping: boolean) =>
  updateCurrentChatState({ isBotTyping });

const setRunningStep = (isRunningStep: boolean) =>
  updateCurrentChatState({ isRunningStep });

const setConnectionError = (isConnectionError: boolean) =>
  updateCurrentChatState({ isConnectionError });

const setCurrentChatSession = ({
  chatId,
  messages,
}: SetCurrentChatSessionOpts) => {
  const values: Partial<CurrentChatState> = {};

  if (chatId !== undefined) values.chatId = chatId;
  if (messages !== undefined) currentChatMessagesVar([...messages]);

  if (chatId !== undefined) updateCurrentChatState(values);
};

const setFlowStep = (value?: ChatFlowStep | null) => {
  if (!value) {
    updateCurrentChatState({ flowStep: null, inputMode: null });
    return;
  }

  if (!isChatFlowStep(value)) {
    throw new Error(
      `You must provide a valid chat flow step, you provided ${String(value)}`,
    );
  }

  updateCurrentChatState({
    flowStep: value,
    inputMode: chatViewPresentation(value).inputMode,
  });
};

const setFlowSteps = (flowSteps: ChatFlowStep[]) =>
  updateCurrentChatState({ flowSteps: [...flowSteps] });

const setInputMode = (inputMode: ChatInputMode) =>
  updateCurrentChatState({ inputMode });

const setFeedbackSubmitted = (feedbackSubmitted: boolean) =>
  updateCurrentChatState({ feedbackSubmitted });

const setBottomSheetVisible = (bottomSheetVisible: boolean) =>
  updateCurrentChatState({ bottomSheetVisible });

const appendLocalMessage = (message: CurrentChatMessage) =>
  currentChatMessagesVar([...currentChatMessagesVar(), message]);

const replaceLastLocalMessage = (message: CurrentChatMessage) =>
  currentChatMessagesVar([...currentChatMessagesVar().slice(0, -1), message]);

const mapCurrentChatState = (chatState: CurrentChatState): CurrentChat => {
  const flowStepIndex = chatState.flowStep
    ? chatState.flowSteps.indexOf(chatState.flowStep)
    : -1;

  const hasCurrentChat =
    flowStepIndex === -1 ||
    flowStepIndex > chatState.flowSteps.indexOf(CHAT_FLOW_STEPS.chatLoad);

  const { viewState, inputMode: defaultInputMode } = chatViewPresentation(
    chatState.flowStep,
  );

  const isChatFlowStepBeforeQuestion =
    chatState.flowStep === null ||
    flowStepsBeforeQuestion.includes(chatState.flowStep);

  return {
    ...chatState,
    viewState,
    inputMode: chatState.inputMode ?? defaultInputMode,
    hasCurrentChat,
    isChatFlowStepBeforeQuestion,
  };
};

const getCurrentChat = () => mapCurrentChatState(currentChatStateVar());

const useCurrentChat = () =>
  mapCurrentChatState(useReactiveVar(currentChatStateVar));

const useLocalMessages = () => useReactiveVar(currentChatMessagesVar);

export {
  appendLocalMessage,
  getCurrentChat,
  setFeedbackSubmitted,
  replaceLastLocalMessage,
  resetCurrentChat,
  setBotTyping,
  setChatLoading,
  setConnectionError,
  setCurrentChatSession,
  setFlowStep,
  setFlowSteps,
  setInputMode,
  setRunningStep,
  setBottomSheetVisible,
  useLocalMessages,
  useCurrentChat,
};
